import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { Game, GameQuestion, PlayerPowerUp } from '@/types/games'
import { TriviaGame } from './modes/trivia-mode'
import { MemoryGame } from './modes/memory-mode'
import { SequenceGame } from './modes/sequence-mode'
import { WordPuzzle } from './modes/word-puzzle-mode'
import { getPlayerPowerUps } from '@/lib/games'
import { useAuth } from '@/lib/auth'

interface GameSessionProps {
  gameId: string
  onComplete: (score: number, time: number) => void
}

export function GameSession({ 
  gameId, 
  onComplete 
}: GameSessionProps) {
  const [game, setGame] = useState<Game | null>(null)
  const [questions, setQuestions] = useState<GameQuestion[]>([])
  const [powerUps, setPowerUps] = useState<PlayerPowerUp[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()

  useEffect(() => {
    if (user) {
      Promise.all([
        loadGame(),
        loadQuestions(),
        loadPowerUps()
      ]).finally(() => setLoading(false))
    }
  }, [user, gameId])

  async function loadGame() {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .eq('id', gameId)
        .single()

      if (error) throw error
      setGame(data)
    } catch (err) {
      setError('Failed to load game')
      console.error(err)
    }
  }

  async function loadQuestions() {
    try {
      const { data, error } = await supabase
        .rpc('get_questions_by_mode', { 
          p_game_id: gameId,
          p_limit: 5 
        })

      if (error) throw error
      if (!data?.length) throw new Error('No questions available')

      setQuestions(data)
    } catch (err) {
      setError('Failed to load questions')
      console.error(err)
    }
  }

  async function loadPowerUps() {
    if (!user) return
    try {
      const playerPowerUps = await getPlayerPowerUps(user.id)
      setPowerUps(playerPowerUps)
    } catch (err) {
      console.error('Failed to load power-ups:', err)
    }
  }

  const handleUsePowerUp = async (_powerUpId: string) => {
    if (!user) return false
    try {
      // Implement power-up logic here
      return true
    } catch (err) {
      console.error('Failed to use power-up:', err)
      return false
    }
  }

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error}</div>
  if (!game) return <div>Game not found</div>

  switch (game.mode) {
    case 'memory':
      return (
        <MemoryGame
          gameId={gameId}
          question={questions[0]}
          powerUps={powerUps}
          onComplete={onComplete}
          onUsePowerUp={handleUsePowerUp}
        />
      )
    case 'sequence':
      return (
        <SequenceGame
          gameId={gameId}
          question={questions[0]}
          powerUps={powerUps}
          onComplete={onComplete}
          onUsePowerUp={handleUsePowerUp}
        />
      )
    case 'word_puzzle':
      return (
        <WordPuzzle
          gameId={gameId}
          questions={questions}
          powerUps={powerUps}
          onComplete={onComplete}
          onUsePowerUp={handleUsePowerUp}
        />
      )
    default:
      return (
        <TriviaGame
          gameId={gameId}
          questions={questions}
          powerUps={powerUps}
          onComplete={onComplete}
          onUsePowerUp={handleUsePowerUp}
        />
      )
  }
}